import { mapZoom } from '../../../src/scripts/helpers/map-zoom';
import { pinClustering } from '../../../src/scripts/helpers/pin-clustering';

document.addEventListener('alpine:init', () => {
    window.Alpine.data('referencesPage', (initialProps) => ({
        ...mapZoom(),
        ...pinClustering(),
        publicUri: initialProps.publicUri,
        regionFilterOptionIdentifiers: initialProps.regionFilterOptionIdentifiers || [],
        countryFilterOptionIdentifiers: initialProps.countryFilterOptionIdentifiers || [],
        categoryFilterOptionIdentifiers: initialProps.categoryFilterOptionIdentifiers || [],
        countryToRegionMap: initialProps.countryToRegionMap || {},
        regionFilter: '',
        countryFilter: '',
        categoryFilter: '',
        showList: true,
        showEmptyMessage: false,
        showFilteredEmptyMessage: false,
        activeAccordionItemId: null,

        init() {
            const params = new URLSearchParams(window.location.search);
            this.regionFilter = params.get('filter[region]') || '';
            if (this.regionFilter !== '' && !this.regionFilterOptionIdentifiers.includes(this.regionFilter)) {
                this.regionFilter = '';
                this.updateFilterInParamsAndReplaceHistoryState('region', '');
            }

            this.countryFilter = params.get('filter[country]') || '';
            if (this.countryFilter !== '' && !this.countryFilterOptionIdentifiers.includes(this.countryFilter)) {
                this.countryFilter = '';
                this.updateFilterInParamsAndReplaceHistoryState('country', '');
            }

            this.categoryFilter = params.get('filter[category]') || '';
            if (this.categoryFilter !== '' && !this.categoryFilterOptionIdentifiers.includes(this.categoryFilter)) {
                this.categoryFilter = '';
                this.updateFilterInParamsAndReplaceHistoryState('category', '');
            }

            this.recomputeViewProperties();

            this.$watch('regionFilter', (value) => {
                this.updateFilterInParamsAndReplaceHistoryState('region', value);
                this.recomputeViewProperties();
            });

            this.$watch('countryFilter', (value) => {
                this.updateFilterInParamsAndReplaceHistoryState('country', value);
                this.recomputeViewProperties();
            });

            this.$watch('categoryFilter', (value) => {
                this.updateFilterInParamsAndReplaceHistoryState('category', value);
                this.recomputeViewProperties();
            });

            const regionSelect = this.$root.querySelector('#region');
            if (regionSelect) {
                regionSelect.addEventListener('change', () => {
                    this.regionFilter = regionSelect.value;
                });
            }
            const countrySelect = this.$root.querySelector('#country');
            if (countrySelect) {
                countrySelect.addEventListener('change', () => {
                    this.countryFilter = countrySelect.value;
                });
            }
            const categorySelect = this.$root.querySelector('#category');
            if (categorySelect) {
                categorySelect.addEventListener('change', () => {
                    this.categoryFilter = categorySelect.value;
                });
            }

            if (this.regionFilter) {
                this.selectTailSelectValue('#region', this.regionFilter);
            }
            if (this.countryFilter) {
                this.selectTailSelectValue('#country', this.countryFilter);
            }
            if (this.categoryFilter) {
                this.selectTailSelectValue('#category', this.categoryFilter);
            }

            // clustering needs the zoom level svg-pan-zoom settles on, so it goes last
            this.initMapZoom();
            this.initPinClustering();
        },

        destroy() {
            this.destroyPinClustering();
        },

        selectTailSelectValue(selectSelector, value) {
            const selectEl = this.$root.querySelector(selectSelector);
            if (!selectEl || !selectEl.nextSibling) return;
            const option = selectEl.nextSibling.querySelector(`li[data-key="${value}"]`);
            if (option) option.click();
        },

        isPinSelectable(countryCodes, categoryNames) {
            if (this.regionFilter !== '') {
                // multi-country references belong to every region their countries map to
                const pinMatchesRegion = countryCodes.some((code) => this.countryToRegionMap[code] === this.regionFilter);
                if (!pinMatchesRegion) {
                    return false;
                }
            }
            if (this.countryFilter !== '') {
                if (!countryCodes.includes(this.countryFilter)) {
                    return false;
                }
            }
            if (this.categoryFilter !== '') {
                const categories = (categoryNames || '').split(',').map((c) => c.trim()).filter(Boolean);
                if (!categories.includes(this.categoryFilter)) {
                    return false;
                }
            }
            return true;
        },

        openAndScrollToAccordionItem(activeAccordionItemId) {
            this.activeAccordionItemId = activeAccordionItemId;

            const card = this.$root.querySelector(
                `.teaser-object[data-reference-id="${activeAccordionItemId}"]`
            );
            if (!card) return;

            card.scrollIntoView({ behavior: 'smooth', block: 'center' });
            card.classList.add('teaser-object--highlight');
            setTimeout(() => {
                card.classList.remove('teaser-object--highlight');
            }, 10000);
        },

        recomputeViewProperties() {
            if (window.neos) {
                return;
            }

            this.showList = !this.filteredListIsEmpty() && !this.listIsEmpty();
            this.showEmptyMessage = this.filterIsEmpty() && this.listIsEmpty();
            this.showFilteredEmptyMessage = this.filteredListIsEmpty();
            // a filter change flips which pins are selectable, so the cluster
            // counts and their disabled state have to be rebuilt
            this.recalculatePinClusters();
        },

        updateFilterInParamsAndReplaceHistoryState(filterName, value) {
            const newLocation = new URL(window.location.href);
            if (value !== '') {
                newLocation.searchParams.set('filter[' + filterName + ']', value);
            } else {
                newLocation.searchParams.delete('filter[' + filterName + ']');
            }
            window.history.replaceState(history.state, '', newLocation.href);
        },

        list() {
            return this.$root.querySelector('.filtered-reference-list__grid');
        },

        filterIsEmpty() {
            return this.regionFilter === '' && this.countryFilter === '' && this.categoryFilter === '';
        },

        get listItems() {
            const list = this.list();
            return list ? Array.from(list.querySelectorAll('.teaser-object')) : [];
        },

        get filteredListItems() {
            return this.listItems.filter((item) => {
                const regionFilters = JSON.parse(item.getAttribute('data-region-filters') || '[]');
                const countryFilters = JSON.parse(item.getAttribute('data-country-filters') || '[]');
                const categoryFilters = JSON.parse(item.getAttribute('data-category-filters') || '[]');
                return (
                    (this.regionFilter === '' || regionFilters.includes(this.regionFilter)) &&
                    (this.countryFilter === '' || countryFilters.includes(this.countryFilter)) &&
                    (this.categoryFilter === '' || categoryFilters.includes(this.categoryFilter))
                );
            });
        },

        listIsEmpty() {
            return this.listItems.length === 0;
        },

        filteredListIsEmpty() {
            return !this.listIsEmpty() && this.filteredListItems.length === 0;
        },

        get publicUriWithFilterParams() {
            const uri = new URL(this.publicUri);
            if (this.regionFilter !== '') {
                uri.searchParams.set('filter[region]', this.regionFilter);
            }
            if (this.countryFilter !== '') {
                uri.searchParams.set('filter[country]', this.countryFilter);
            }
            if (this.categoryFilter !== '') {
                uri.searchParams.set('filter[category]', this.categoryFilter);
            }
            return uri.href;
        },
    }));
});
